import React, { useEffect } from 'react';
import { Shield, XCircle, AlertCircle } from 'lucide-react';

const RefundPolicyPage = () => {
  useEffect(() => {
    document.title = 'Refund Policy | Indian Cyber Club Academy';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-black min-h-screen pt-20 sm:pt-24 pb-16">
      <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-red-500" />
            <span className="text-red-500 font-semibold text-sm uppercase tracking-wider">Legal Policy</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-black text-white mb-4">
            Refund <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Policy</span>
          </h1>
          <p className="text-gray-400">Last Updated: January 30, 2025</p>
        </div>

        {/* Important Notice */}
        <div className="bg-gradient-to-br from-red-600/10 to-orange-600/10 border-2 border-red-500/30 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-bold text-white mb-2">Important Notice</h3>
              <p className="text-gray-300 leading-relaxed">
                All course fees paid to Indian Cyber Club Academy are <strong className="text-red-500">non-refundable</strong> under any circumstances. Please read this policy carefully before enrolling in any course.
              </p>
            </div>
          </div>
        </div>

        {/* Policy Content */}
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8 space-y-8">
          {/* Section 1 */}
          <div>
            <h2 className="text-2xl font-black text-white mb-4 flex items-center gap-2">
              <XCircle className="w-6 h-6 text-red-500" />
              No Refund Policy
            </h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Indian Cyber Club Academy operates under a strict <strong>no refund policy</strong>. Once a course fee is paid, it is considered final and non-refundable, regardless of the circumstances.
            </p>
            <p className="text-gray-300 leading-relaxed">
              By enrolling in any course and making the payment, you acknowledge and agree to this no refund policy.
            </p>
          </div>

          {/* Section 2 */}
          <div>
            <h2 className="text-2xl font-black text-white mb-4">Circumstances with No Refunds</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Refunds will <strong>NOT be issued</strong> under any of the following circumstances:
            </p>
            <ul className="space-y-3">
              {[
                'Student decides not to attend the course after payment',
                'Student is unable to complete the course for personal reasons',
                'Student is dissatisfied with course content or delivery',
                'Technical issues on the student\'s side (internet, device, etc.)',
                'Change of mind or change in career plans',
                'Failure to attend classes or access course materials',
                'Lack of time or inability to dedicate required effort',
                'Course schedule conflicts or timing issues',
                'Relocation or travel that prevents course participation',
                'Any other personal, professional, or medical reasons'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 3 */}
          <div>
            <h2 className="text-2xl font-black text-white mb-4">Course Cancellation by Academy</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              In the rare event that Indian Cyber Club Academy cancels a course due to unforeseen circumstances:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-300">
                <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                <span>Students will be offered an alternative batch or course of equivalent value</span>
              </li>
              <li className="flex items-start gap-3 text-gray-300">
                <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                <span>Course credits may be issued for future courses</span>
              </li>
              <li className="flex items-start gap-3 text-gray-300">
                <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                <span>Refunds will only be considered at the sole discretion of the academy management</span>
              </li>
            </ul>
          </div>

          {/* Section 4 */}
          <div>
            <h2 className="text-2xl font-black text-white mb-4">Before You Enroll</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We strongly recommend that you:
            </p>
            <ul className="space-y-3">
              {[
                'Carefully review course details, curriculum, and duration',
                'Ensure you have the required time commitment',
                'Verify technical requirements and prerequisites',
                'Contact us for any questions or clarifications',
                'Understand that fees are non-refundable once paid',
                'Make an informed decision before making payment'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 5 */}
          <div>
            <h2 className="text-2xl font-black text-white mb-4">Payments & Acknowledgment</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              By making a payment for any course at Indian Cyber Club Academy, you:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-300">
                <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                <span>Acknowledge that you have read and understood this refund policy</span>
              </li>
              <li className="flex items-start gap-3 text-gray-300">
                <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                <span>Agree that all fees are final and non-refundable</span>
              </li>
              <li className="flex items-start gap-3 text-gray-300">
                <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                <span>Waive any right to request a refund under any circumstances</span>
              </li>
              <li className="flex items-start gap-3 text-gray-300">
                <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                <span>Accept full responsibility for completing the enrolled course</span>
              </li>
            </ul>
          </div>

          {/* Section 6 */}
          <div>
            <h2 className="text-2xl font-black text-white mb-4">Contact for Queries</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              If you have any questions about this refund policy or need clarification before enrolling, please contact us:
            </p>
            <div className="bg-gray-950/50 border border-red-600/30 rounded-lg p-4 space-y-2">
              <p className="text-white">
                <strong>Indian Cyber Club Academy</strong>
              </p>
              <p className="text-gray-300">
                Phone: <a href="tel:+919694612895" className="text-red-500 hover:underline">+91-9694612895</a>
              </p>
              <p className="text-gray-300">
                WhatsApp: <a href="https://wa.me/919694612895" target="_blank" rel="noopener noreferrer" className="text-red-500 hover:underline">+91-9694612895</a>
              </p>
              <p className="text-gray-300">
                Location: Jaipur, Rajasthan, India
              </p>
            </div>
          </div>

          {/* Final Note */}
          <div className="bg-red-600/10 border border-red-500/30 rounded-lg p-4">
            <p className="text-sm text-gray-300 leading-relaxed">
              <strong className="text-red-500">Please Note:</strong> This refund policy is effective from the date mentioned above and applies to all course enrollments. Indian Cyber Club Academy reserves the right to modify this policy at any time. Any changes will be updated on this page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicyPage;
